"use client";

import Link from "next/link";
import { ArrowRight, Check, DollarSign, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHero from "@/components/landing/PageHero";
import { AnimatedSection } from "@/hooks/useScrollAnimation";
const featureHero = "/assets/smiling-workers.jpg";
const aboutVirtualCare = "/assets/family-military.jpg";
const aboutFamily = "/assets/about-family.jpg";
const aboutCommunity = "/assets/video-call.png";
const aboutBusiness = "/assets/about-businesses-card.jpg";
const ashleyStuhr = "/assets/ashley-stuhr.png";


const About = () => {
  const { t } = useTranslation();

  const whyVirtualFeatures = [
    {
      icon: Clock,
      title: t("about.whyV1Title"),
      description: t("about.whyV1Desc"),
    },
    {
      icon: DollarSign,
      title: t("about.whyV2Title"),
      description: t("about.whyV2Desc"),
    },
    {
      icon: Shield,
      title: t("about.whyV3Title"),
      description: t("about.whyV3Desc"),
    },
  ];

  const solutions = [
    t("about.sol1"),
    t("about.sol2"),
    t("about.sol3"),
    t("about.sol4"),
    t("about.sol5"),
  ];
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        title={t("about.heroTitle")}
        subtitle={t("about.heroSubtitle")}
        backgroundImage={featureHero}
      />

      {/* Mission — Split Layout */}
      <section className="py-6 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden h-[360px] lg:h-[480px] flex items-center justify-center bg-white">
                <img src={aboutCommunity} alt="Diverse group of workers giving thumbs up" className="w-full h-full object-contain" loading="lazy" />
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-4">{t("about.missionHeading")}</h2>
              <p className="font-body text-base text-foreground/70 leading-relaxed mb-4">{t("about.missionDesc")}</p>
              <div className="rounded-2xl p-6 mt-6" style={{ background: "linear-gradient(135deg, hsl(var(--accent-blue)) 0%, hsl(var(--primary)) 100%)" }}>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{t("about.notInsuranceHeading")}</h3>
                <p className="font-body text-sm text-white/80 mb-2">{t("about.notInsuranceDesc1")}</p>
                <p className="font-body text-sm text-white/80 leading-relaxed">{t("about.notInsuranceDesc2")}</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Founder — Dark Gradient Card */}
      <section className="bg-[#f5f8fc] py-20 px-6 lg:px-12">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <div
              className="relative rounded-[24px] overflow-hidden p-8 lg:p-14"
              style={{
                background: "linear-gradient(135deg, #0a1628 0%, #0f2347 45%, #162d5a 100%)",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.35)",
              }}
            >
              {/* Subtle inner glow overlay */}
              <div
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                  background: "radial-gradient(ellipse 70% 60% at 30% 50%, rgba(59,130,246,0.18), transparent 70%)",
                }}
              />
              {/* Noise texture */}
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.03]"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                  backgroundSize: "256px 256px",
                }}
              />
              {/* 2-column layout */}
              <div className="relative z-10 flex flex-col lg:flex-row gap-10 lg:gap-14 items-stretch">
                {/* LEFT: Image */}
                <div className="w-full lg:w-[42%] flex-shrink-0 flex">
                  <div
                    className="rounded-[16px] overflow-hidden w-full relative shadow-2xl"
                    style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 20px 60px rgba(0,0,0,0.4)" }}
                  >
                    <img
                      src={ashleyStuhr}
                      alt="Ashley Stuhr, Founder of Legacy Healthcare Solutions"
                      className="w-full h-full object-cover object-top min-h-[280px]"
                      style={{ minHeight: "280px" }}
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
                  </div>
                </div>
                {/* RIGHT: Content */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <p className="font-button text-xs tracking-[0.18em] uppercase text-white/45 mb-4">
                    {t("about.founderLabel")}
                  </p>
                  <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-white leading-snug mb-4">
                    {t("about.founderHeading")}
                  </h2>
                  <p className="font-body text-sm text-white/75 leading-relaxed mb-4">
                    {t("about.founderDesc1")}
                  </p>
                  <p className="font-body text-sm text-white/75 leading-relaxed mb-4">
                    {t("about.founderDesc2")}
                  </p>
                  <p className="font-body text-sm text-white/75 leading-relaxed">
                    {t("about.founderDesc3")}
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Virtual Healthcare — Feature Cards */}
      <section className="py-20 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-3">{t("about.whyVirtualHeading")}</h2>
            <p className="font-body text-base text-foreground/70 text-center max-w-2xl mx-auto mb-12">{t("about.whyVirtualSubtitle")}</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyVirtualFeatures.map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 100}>
                <div className="group bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:bg-accent-blue cursor-pointer h-full">
                  <div className="w-12 h-12 bg-accent-blue/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-accent-blue group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-2 text-foreground group-hover:text-white transition-colors duration-300">{feature.title}</h3>
                  <p className="font-body text-xs text-foreground/70 group-hover:text-white/80 leading-relaxed transition-colors duration-300">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={400}>
            <p className="font-heading font-semibold text-base text-foreground text-center mt-10">{t("about.accessNote")}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Our Solutions — Split Layout (reversed) */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection delay={100}>
              <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-4">{t("about.solutionsHeading")}</h2>
              <p className="font-body text-base text-foreground/70 mb-6">{t("about.solutionsDesc")}</p>
              <ul className="space-y-3 mb-6">
                {solutions.map((item) => (
                  <li key={item} className="font-body text-sm text-foreground/80 flex items-start gap-2"><Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>
              <p className="font-body text-sm text-foreground/60 italic">{t("about.solutionsNote")}</p>
            </AnimatedSection>
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden h-[360px] lg:h-[480px]">
                <img src={aboutVirtualCare} alt="Family accessing virtual healthcare from home on computer" className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* For Families & Businesses — Side-by-Side Cards */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-[1100px] mx-auto">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground text-center mb-12">{t("about.whoWeServe")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Families Card */}
            <AnimatedSection delay={0}>
              <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full border border-border">
                <div className="h-56 overflow-hidden">
                  <img src={aboutFamily} alt="Multigenerational family relaxing together on couch" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl text-foreground mb-3">{t("about.familiesCardTitle")}</h3>
                  <ul className="space-y-2 mb-6">
                    {[t("about.familiesItem1"), t("about.familiesItem2"), t("about.familiesItem3"), t("about.familiesItem4")].map((item) => (
                      <li key={item} className="font-body text-sm text-foreground/70 flex items-start gap-2"><Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}</li>
                    ))}
                  </ul>
                  <Link href="/individuals">
                    <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-4 py-4 font-button text-sm cta-hover w-full whitespace-normal h-auto leading-snug text-center">
                      {t("about.familiesCta")} <ArrowRight className="w-4 h-4 ml-1 flex-shrink-0 inline" />
                    </Button>
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            {/* Businesses Card */}
            <AnimatedSection delay={150}>
              <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full border border-border">
                <div className="h-56 overflow-hidden">
                  <img src={aboutBusiness} alt="Cheerful coworkers collaborating on a project" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl text-foreground mb-3">{t("about.businessCardTitle")}</h3>
                  <ul className="space-y-2 mb-6">
                    {[t("about.businessItem1"), t("about.businessItem2"), t("about.businessItem3"), t("about.businessItem4")].map((item) => (
                      <li key={item} className="font-body text-sm text-foreground/70 flex items-start gap-2"><Check className="w-4 h-4 text-accent-blue mt-0.5 flex-shrink-0" />{item}</li>
                    ))}
                  </ul>
                  <Link href="/businesses">
                    <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-4 py-4 font-button text-sm cta-hover w-full whitespace-normal h-auto leading-snug text-center">
                      {t("about.businessCta")} <ArrowRight className="w-4 h-4 ml-1 flex-shrink-0 inline" />
                    </Button>
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Commitment — Bottom CTA */}
      <section className="py-20 px-6 lg:px-12 bg-secondary">
        <div className="max-w-[800px] mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-4">{t("about.commitmentHeading")}</h2>
            <p className="font-body text-base text-foreground/70 leading-relaxed mb-4">{t("about.commitmentDesc1")}</p>
            <p className="font-body text-base text-foreground/70 leading-relaxed mb-8">{t("about.commitmentDesc2")}</p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[t("about.commit1"), t("about.commit2"), t("about.commit3"), t("about.commit4")].map((item, i) => (
                <div key={item} className="bg-white rounded-xl p-4 text-center">
                  <Check className="w-5 h-5 text-accent-blue mx-auto mb-2" />
                  <p className="font-body text-sm text-foreground/80 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <p className="font-heading font-semibold text-base text-foreground mb-8">{t("about.commitmentPurpose")}</p>
            <Link href="/contact">
              <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-8 py-4 font-button text-base cta-hover">
                {t("nav.contactUs")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
